<template>
  <div class="task-view">
    <div class="card">
      <p class="title">{{ $t('TICKET.FORM.COMMUNICATIONS.FORM_TITLE') }}</p>

      <div class="row">
        <div class="card-form card-1">
          <p>{{ $t('TICKET.FORM.COMMUNICATIONS.CONTACT') }}</p>
          <multiselect
            v-model="selectedContactId"
            :options="ticketContactOptions"
            track-by="id"
            label="name"
            selected-label=""
            select-label=""
            deselect-label=""
            :allow-empty="false"
            :placeholder="$t('FORMS.MULTISELECT.SELECT')"
          />
          <div class="contact-select-wrap">
            <p v-if="showContactRequiredError" class="field-error">
              {{ $t('TICKET.FORM.COMMUNICATIONS.CONTACT') }} é obrigatório
            </p>
          </div>
        </div>

        <div class="card-form card-2">
          <p>{{ $t('TICKET.FORM.COMMUNICATIONS.SCHEDULE_AT') }}</p>
          <woot-date-time-picker
            :value="scheduleAt"
            :confirm-text="$t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.CONFIRM')"
            :placeholder="$t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.PLACEHOLDER_DATE')"
            @change="onChangeDate"
          />
        </div>
      </div>

      <div class="row">
        <div class="card-form card-1">
          <p>{{ $t('TICKET.FORM.COMMUNICATIONS.CHANNEL') }}</p>
          <v-select
            v-model="channel"
            :options="channelOptions"
            label="label"
            :reduce="option => option.value"
            :clearable="false"
          />
        </div>

        <div v-if="isWhatsappSelected" class="card-form card-2">
          <p>{{ $t('NEW_CONVERSATION.FORM.INBOX.LABEL') }}</p>
          <div class="multiselect-wrap--small">
            <multiselect
              v-model="targetInbox"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              selected-label=""
              select-label=""
              deselect-label=""
              :max-height="160"
              :close-on-select="true"
              :options="[...inboxes]"
              :disabled="!isWhatsappSelected"
            >
              <template slot="singleLabel" slot-scope="{ option }">
                <inbox-dropdown-item
                  v-if="option.name"
                  :name="option.name"
                  :inbox-identifier="computedInboxSource(option)"
                  :channel-type="option.channel_type"
                />
                <span v-else>
                  {{ $t('NEW_CONVERSATION.FORM.INBOX.PLACEHOLDER') }}
                </span>
              </template>
              <template slot="option" slot-scope="{ option }">
                <inbox-dropdown-item
                  :name="option.name"
                  :inbox-identifier="computedInboxSource(option)"
                  :channel-type="option.channel_type"
                />
              </template>
            </multiselect>
          </div>
        </div>
      </div>

      <div v-if="channel === 'email'" class="row">
        <div class="card-form card-full">
          <p>{{ $t('TICKET.FORM.COMMUNICATIONS.SUBJECT') }}</p>
          <input
            v-model.trim="subject"
            type="text"
            :placeholder="$t('TICKET.FORM.COMMUNICATIONS.SUBJECT_PLACEHOLDER')"
          />
        </div>
      </div>

      <div v-if="channel === 'email'" class="row">
        <div class="card-form card-full attachments-wrap">
          <p>{{ $t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON') }}</p>
          <input
            ref="emailAttachmentInput"
            type="file"
            multiple
            class="hidden-file-input"
            @change="onEmailAttachmentSelected"
          />
          <div class="gmail-attachments-toolbar">
            <button
              type="button"
              class="gmail-attach-btn"
              @click="openEmailAttachmentSelector"
            >
              📎 {{ $t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON') }}
            </button>
            <span v-if="emailAttachments.length" class="gmail-attach-count">
              {{ emailAttachments.length }} anexo(s)
            </span>
          </div>

          <div v-if="emailAttachments.length" class="gmail-attachments-list">
            <div
              v-for="(attachment, index) in emailAttachments"
              :key="attachment.id || index"
              class="gmail-attachment-chip"
            >
              <span class="chip-icon">📄</span>
              <span
                class="chip-name"
                :title="attachment.resource && attachment.resource.name"
              >
                {{ attachment.resource && attachment.resource.name }}
              </span>
              <span class="chip-size">
                {{ formatAttachmentSize(attachment.resource) }}
              </span>
              <button
                type="button"
                class="chip-remove"
                @click="removeEmailAttachment(index)"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isWhatsappSelected && hasWhatsappTemplates" class="row">
        <div class="card-form card-full template-wrap">
          <whatsapp-templates
            :contact="contactWithInboxes"
            :inbox-id="selectedInbox.inbox.id"
            :only-select="true"
            @on-send="onSelectWhatsAppTemplate"
            @pickTemplate="onPickWhatsAppTemplate"
          />
        </div>
      </div>

      <div
        v-if="
          channel === 'email' || !isWhatsappSelected || !hasWhatsappTemplates
        "
        class="row"
      >
        <div class="card-form card-full">
          <p>{{ $t('TICKET.FORM.COMMUNICATIONS.MESSAGE') }}</p>
          <textarea
            v-model.trim="body"
            rows="4"
            :placeholder="$t('TICKET.FORM.COMMUNICATIONS.MESSAGE_PLACEHOLDER')"
          />
        </div>
      </div>

      <div class="footer">
        <woot-button
          color-scheme="primary"
          :is-disabled="disabledSendNow"
          :title="$t('TICKET.FORM.COMMUNICATIONS.SEND')"
          :is-loading="isSubmitting"
          @click="onSendNow"
        >
          {{ $t('TICKET.FORM.COMMUNICATIONS.SEND') }}
        </woot-button>

        <woot-button
          color-scheme="warning"
          :is-disabled="disabledSchedule"
          :title="$t('TICKET.FORM.COMMUNICATIONS.SCHEDULE')"
          :is-loading="isSubmitting"
          @click="onSchedule"
        >
          {{ $t('TICKET.FORM.COMMUNICATIONS.SCHEDULE') }}
        </woot-button>
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from 'vue-select';
import { DirectUpload } from 'activestorage';
import WootDateTimePicker from 'dashboard/components/ui/DateTimePicker.vue';
import alertMixin from 'shared/mixins/alertMixin';
import WhatsappTemplates from 'dashboard/routes/dashboard/conversation/contact/WhatsappTemplates.vue';
import InboxDropdownItem from 'dashboard/components/widgets/InboxDropdownItem';
import { getInboxSource } from 'dashboard/helper/inbox';
import {
  buildAccountDirectUploadUrl,
  buildBlobUrl,
} from 'dashboard/helper/directUploadHelper';
import { MAXIMUM_FILE_UPLOAD_SIZE } from 'shared/constants/messages';
import { checkFileSizeLimit, formatBytes } from 'shared/helpers/FileHelper';
import { mapGetters } from 'vuex';

export default {
  name: 'ScheduleMessageView',
  components: {
    vSelect,
    WootDateTimePicker,
    WhatsappTemplates,
    InboxDropdownItem,
  },
  mixins: [alertMixin],
  props: {
    ticket: {
      type: Object,
      default: () => ({}),
    },
    contact: {
      type: Object,
      default: () => ({}),
    },
    targetContact: {
      type: Object,
      default: null,
    },
    ticketContactsRefreshKey: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      selectedContactId: null,
      ticketContacts: [],
      channel: 'whatsapp',
      subject: '',
      body: '',
      scheduleAt: null,
      isSubmitting: false,
      scheduledCommunications: [],
      targetInbox: {},
      hasWhatsappTemplates: null,
      selectedTemplatePayload: null,
      whatsappTemplateSelected: false,
      emailAttachments: [],
      hasTriedSubmit: false,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      activeInbox: 'getSelectedInbox',
      currentUser: 'getCurrentUser',
    }),
    selectedContact() {
      if (!this.selectedContactId) {
        return {};
      }

      if (typeof this.selectedContactId === 'object') {
        return this.selectedContactId;
      }

      if (this.selectedContactId) {
        return (
          this.ticketContactOptions.find(
            contact => contact.id === this.selectedContactId
          ) || {}
        );
      }

      return {};
    },
    ticketContactOptions() {
      const linkedContacts = Array.isArray(this.ticketContacts)
        ? this.ticketContacts
        : [];

      const contacts = [
        ...linkedContacts,
        ...(linkedContacts.length ? [] : [this.ticket?.contact, this.contact]),
      ].filter(contact => contact?.id);

      const uniqueById = new Map();
      contacts.forEach(contact => {
        if (!uniqueById.has(contact.id)) {
          uniqueById.set(contact.id, contact);
        }
      });

      return Array.from(uniqueById.values());
    },
    contactWithInboxes() {
      const contactState = this.$store.getters['contacts/getContact'](
        this.selectedContact?.id
      );
      return { ...this.selectedContact, ...(contactState || {}) };
    },
    selectedContactName() {
      return this.selectedContact?.name || '-';
    },
    canSendEmail() {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
        this.selectedContact?.email || ''
      );
    },
    whatsappInboxes() {
      const inboxList = this.contactWithInboxes.contactableInboxes || [];
      return inboxList.filter(inbox =>
        this.isInboxCompatibleWithChannel(inbox.inbox, 'whatsapp')
      );
    },
    canSendWhatsapp() {
      return this.whatsappInboxes.length > 0;
    },
    inboxes() {
      const inboxList = this.contactWithInboxes.contactableInboxes || [];
      return inboxList
        .filter(inbox =>
          this.isInboxCompatibleWithChannel(inbox.inbox, this.channel)
        )
        .map(inbox => ({
          ...inbox.inbox,
          sourceId: inbox.source_id,
        }));
    },
    selectedInbox() {
      const inboxList = this.contactWithInboxes.contactableInboxes || [];
      return (
        inboxList.find(inbox => inbox.inbox.id === this.targetInbox?.id) || {
          inbox: {},
        }
      );
    },
    isWhatsappSelected() {
      return this.channel === 'whatsapp';
    },
    isEmailSelected() {
      return this.channel === 'email';
    },
    channelOptions() {
      const options = [];

      if (this.canSendWhatsapp) {
        options.push({ value: 'whatsapp', label: 'WhatsApp' });
      }

      if (this.canSendEmail) {
        options.push({ value: 'email', label: 'E-mail' });
      }

      if (!options.length) {
        return [{ value: 'whatsapp', label: 'WhatsApp' }];
      }

      return options;
    },
    isFormInvalid() {
      if (!this.ticket?.id || !this.selectedContact?.id) return true;

      if (this.channel === 'email') {
        return (
          !this.selectedContact?.email ||
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.selectedContact.email) ||
          !this.subject ||
          !this.body
        );
      }

      if (!this.targetInbox?.id) return true;

      if (this.hasWhatsappTemplates) {
        return !this.selectedTemplatePayload?.message;
      }

      if (!this.body) return true;

      return false;
    },
    showContactRequiredError() {
      return this.hasTriedSubmit && !this.selectedContact?.id;
    },
    disabledSendNow() {
      return this.isFormInvalid;
    },
    disabledSchedule() {
      return this.isFormInvalid || !this.scheduleAt;
    },
  },
  watch: {
    ticket: {
      immediate: true,
      handler() {
        this.selectedContactId = null;
        this.fetchTicketContacts();
      },
    },
    ticketContactsRefreshKey() {
      this.fetchTicketContacts();
    },
    async targetInbox(inbox) {
      if (this.isWhatsappSelected) {
        this.hasWhatsappTemplates = await this.needTemplate(inbox);
        this.selectedTemplatePayload = null;
        this.whatsappTemplateSelected = false;
      }
    },
    channel(value) {
      this.hasWhatsappTemplates = null;
      this.selectedTemplatePayload = null;
      this.subject = '';
      this.body = '';
      this.emailAttachments = [];

      if (!value) {
        this.targetInbox = {};
        return;
      }

      if (value === 'email') {
        this.targetInbox = {};
        return;
      }

      const currentInboxIsValid = this.inboxes.some(
        inbox => inbox.id === this.targetInbox?.id
      );

      if (currentInboxIsValid) return;

      const activeInbox = this.$store.getters['inboxes/getInbox'](
        this.activeInbox
      );
      if (this.isInboxCompatibleWithChannel(activeInbox)) {
        this.targetInbox = activeInbox;
        return;
      }

      this.targetInbox = this.inboxes[0] || {};
    },
    channelOptions: {
      immediate: true,
      handler(options) {
        if (!options.length) {
          this.channel = 'whatsapp';
          return;
        }

        const hasCurrentChannel = options.some(
          option => option.value === this.channel
        );

        if (!hasCurrentChannel) {
          const whatsappOption = options.find(
            option => option.value === 'whatsapp'
          );
          this.channel = whatsappOption?.value || options[0].value;
        }
      },
    },
    selectedContact: {
      immediate: true,
      async handler(contact) {
        if (contact?.id) {
          this.hasTriedSubmit = false;
        }
        if (!contact?.id) return;
        await this.$store.dispatch(
          'contacts/fetchContactableInbox',
          contact.id
        );

        const activeInbox = this.$store.getters['inboxes/getInbox'](
          this.activeInbox
        );

        if (this.channel === 'email') {
          this.targetInbox = {};
          return;
        }

        if (this.isInboxCompatibleWithChannel(activeInbox)) {
          this.targetInbox = activeInbox;
          return;
        }

        this.targetInbox = this.inboxes[0] || {};
      },
    },
  },
  methods: {
    normalizeTicketContactsResponse(response) {
      if (Array.isArray(response)) return response;
      if (Array.isArray(response?.payload)) return response.payload;
      if (Array.isArray(response?.data)) return response.data;
      return [];
    },
    async fetchTicketContacts() {
      if (!this.ticket?.id) {
        this.ticketContacts = [];
        return;
      }

      try {
        const response = await this.$store.dispatch('getTicketContacts', {
          ticketId: this.ticket.id,
        });
        this.ticketContacts = this.normalizeTicketContactsResponse(response);
      } catch (_error) {
        this.ticketContacts = [];
      }
    },
    isInboxCompatibleWithChannel(inbox, channel = this.channel) {
      if (!inbox?.channel_type) return false;

      if (channel === 'whatsapp') {
        return (
          inbox.channel_type === 'Channel::Whatsapp' ||
          inbox.channel_type === 'Channel::Api'
        );
      }

      if (channel === 'email') {
        return inbox.channel_type === 'Channel::Email';
      }

      return false;
    },
    computedInboxSource(inbox) {
      if (!inbox?.channel_type) return '';
      return getInboxSource(inbox.channel_type, inbox.phone_number, inbox);
    },
    async needTemplate(inbox) {
      if (inbox?.id && inbox?.channel_type === 'Channel::Whatsapp') {
        const params = {
          contactId: this.selectedContact?.id,
          inboxId: inbox?.id,
          accountId: this.accountId,
        };
        const needTemplate = await this.$store.dispatch(
          'contactConversations/needTemplate',
          params
        );
        return (
          !!this.selectedInbox.inbox?.message_templates && needTemplate.result
        );
      }

      return false;
    },
    onPickWhatsAppTemplate(value) {
      this.whatsappTemplateSelected = value;
    },
    onSelectWhatsAppTemplate(payload) {
      this.selectedTemplatePayload = payload;
      this.body = payload?.message || '';
    },
    onChangeDate(value) {
      this.scheduleAt = value;
    },
    openEmailAttachmentSelector() {
      if (this.$refs.emailAttachmentInput) {
        this.$refs.emailAttachmentInput.click();
      }
    },
    formatAttachmentSize(file) {
      const size = file?.size || file?.byte_size || 0;
      return formatBytes(size, 0);
    },
    resetForm() {
      this.selectedContactId = null;
      this.channel = 'whatsapp';
      this.subject = '';
      this.body = '';
      this.scheduleAt = null;
      this.selectedTemplatePayload = null;
      this.whatsappTemplateSelected = false;
      this.emailAttachments = [];
      this.hasTriedSubmit = false;
    },
    async onEmailAttachmentSelected(event) {
      const files = Array.from(event?.target?.files || []);
      if (!files.length) return;

      const validFiles = files.filter(file => {
        const wrappedFile = { file };
        if (checkFileSizeLimit(wrappedFile, MAXIMUM_FILE_UPLOAD_SIZE)) {
          return true;
        }

        this.showAlert(
          this.$t('CONVERSATION.FILE_SIZE_LIMIT', {
            MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE: MAXIMUM_FILE_UPLOAD_SIZE,
          })
        );
        return false;
      });

      await validFiles.reduce(
        (promise, file) => promise.then(() => this.uploadEmailAttachment(file)),
        Promise.resolve()
      );

      if (this.$refs.emailAttachmentInput) {
        this.$refs.emailAttachmentInput.value = '';
      }
    },
    async uploadEmailAttachment(file) {
      const uploadUrl = buildAccountDirectUploadUrl(this.accountId);

      await new Promise(resolve => {
        const upload = new DirectUpload(file, uploadUrl, {
          directUploadWillCreateBlobWithXHR: xhr => {
            xhr.setRequestHeader(
              'api_access_token',
              this.currentUser?.access_token
            );
          },
        });

        upload.create((error, blob) => {
          if (error) {
            this.showAlert(error);
            resolve();
            return;
          }

          const reader = new FileReader();
          reader.onloadend = () => {
            this.emailAttachments.push({
              id: blob.signed_id,
              resource: file,
              thumb: reader.result,
              blobSignedId: blob.signed_id,
              url: buildBlobUrl(blob.signed_id, blob.filename),
              type: file.type?.split('/')[0] || 'document',
            });
            resolve();
          };
          reader.readAsDataURL(file);
        });
      });
    },
    removeEmailAttachment(index) {
      this.emailAttachments = this.emailAttachments.filter(
        (_item, currentIndex) => currentIndex !== index
      );
    },
    async submitCommunication({ scheduleAt = null } = {}) {
      const isScheduledAction = !!scheduleAt;
      if (
        (isScheduledAction ? this.disabledSchedule : this.disabledSendNow) ||
        this.isSubmitting
      ) {
        return;
      }

      this.isSubmitting = true;
      try {
        const whatsappPayload = this.selectedTemplatePayload || {};
        const payload = {
          channel: this.channel,
          body:
            this.channel === 'whatsapp' && whatsappPayload.message
              ? whatsappPayload.message
              : this.body,
          subject: this.channel === 'email' ? this.subject : null,
          schedule_at: scheduleAt,
          timezone_offset: new Date().getTimezoneOffset(),
          contact_id: this.selectedContact.id,
          inbox_id: this.channel === 'whatsapp' ? this.targetInbox?.id : null,
          files:
            this.channel === 'email'
              ? this.emailAttachments
                  .map(attachment => attachment.blobSignedId)
                  .filter(Boolean)
              : [],
          template_params:
            this.channel === 'whatsapp'
              ? whatsappPayload.templateParams || null
              : null,
        };

        const response = await this.$store.dispatch('sendTicketCommunication', {
          ticketId: this.ticket.id,
          communication: payload,
        });

        if (response?.status === 'sent') {
          this.showAlert(this.$t('TICKET.FORM.COMMUNICATIONS.SENT_SUCCESS'));
          this.resetForm();
          this.$emit('clear-target-contact');
          return;
        }

        if (response?.status === 'scheduled') {
          this.showAlert(
            this.$t('TICKET.FORM.COMMUNICATIONS.SCHEDULED_SUCCESS')
          );
          this.scheduledCommunications.unshift({
            id: Date.now(),
            contact_name: this.selectedContactName,
            channel: this.channel,
            schedule_at: scheduleAt,
          });
          this.resetForm();
          this.$emit('clear-target-contact');
        }
      } catch (error) {
        this.showAlert(
          error?.message || this.$t('TICKET.FORM.COMMUNICATIONS.SEND_ERROR')
        );
      } finally {
        this.isSubmitting = false;
      }
    },
    async onSendNow() {
      this.hasTriedSubmit = true;
      await this.submitCommunication();
    },
    async onSchedule() {
      this.hasTriedSubmit = true;
      await this.submitCommunication({ scheduleAt: this.scheduleAt });
    },
  },
};
</script>

<style lang="scss" scoped>
.card-1 {
  width: 50%;
}
.card-2 {
  width: 48%;
  margin-left: 1rem;
}
.card-full {
  width: 100%;
}
.card-form {
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 1.6rem;
}

.contact-select-wrap {
  width: 60%;
}

.field-error {
  color: var(--r-500);
  font-size: 1.2rem;
  margin-top: 0.4rem;
}
.card {
  overflow: unset !important;

  .title {
    font-weight: bold;
    margin-bottom: 1rem;
  }
}

input,
textarea {
  margin: 0 !important;
}

textarea {
  min-height: 8rem;
}

.v-select {
  font-size: 1.4rem;
  width: 100%;
}

.multiselect-wrap--small {
  width: 100%;
}

.template-wrap {
  display: block;
}

.template-wrap :deep(.row) {
  margin: 0;
}

.footer {
  display: flex;
  justify-content: flex-end;
  align-content: center !important;
  gap: 1rem;
  width: 100%;
}

.task-view h2 {
  font-weight: 300;
}

.schedule-history {
  margin-top: 1rem;
}

.schedule-history-item {
  background: var(--white);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  padding: 0.8rem;
  margin-bottom: 0.5rem;
}

.attachments-wrap {
  align-items: flex-start;
  flex-direction: column;
  gap: 0.8rem;
}

.hidden-file-input {
  display: none;
}

.gmail-attachments-toolbar {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.gmail-attach-btn {
  border: 1px solid var(--color-border);
  border-radius: 2rem;
  background: var(--white);
  color: var(--s-900);
  padding: 0.5rem 1rem;
  font-size: 1.3rem;
  cursor: pointer;
}

.gmail-attach-btn:hover {
  background: var(--color-background-light);
}

.gmail-attach-count {
  color: var(--s-700);
  font-size: 1.2rem;
}

.gmail-attachments-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
}

.gmail-attachment-chip {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  border: 1px solid var(--color-border);
  border-radius: 1.6rem;
  padding: 0.4rem 0.8rem;
  background: var(--white);
  max-width: 26rem;
}

.chip-icon {
  font-size: 1.2rem;
}

.chip-name {
  font-size: 1.2rem;
  max-width: 14rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chip-size {
  font-size: 1.1rem;
  color: var(--s-700);
}

.chip-remove {
  border: none;
  background: transparent;
  font-size: 1.6rem;
  line-height: 1;
  cursor: pointer;
  color: var(--s-700);
}
</style>
